/**
 * Open/closed state for the inspector panel, and the gate that keeps it off.
 *
 * The panel is opt-in twice over: a display preference the user sets once, and a
 * per-surface decision by the host. Both have to be true, and `enabled` is the
 * single boolean the host resolves them into. While it is false this hook is
 * inert — `open()` does nothing and `isOpen` never becomes true — so a surface
 * that forgets to hide its click affordance still cannot pop a panel the user
 * turned off.
 *
 * Once open, the panel FOLLOWS the workspace focus store: clicking another row or
 * node re-seeds the target instead of leaving a stale panel next to a new
 * selection. It does not open on focus alone, because focus changes on ordinary
 * row clicks and a sheet that appeared every time would be unusable.
 */
import * as React from "react";
import { useOptionalInvestigationFocus } from "@authe/workspace";
import { subjectFromFocus, targetKey, type InspectorTarget } from "./model";

/**
 * What the panel should actually show, given the gate and the local state.
 *
 * Pure and exported so the gate is testable on its own: "off means closed" is
 * the guarantee the whole opt-in rests on, and it should not need a rendered
 * component to prove.
 */
export function resolveInspectorView(input: {
  enabled: boolean;
  requestedOpen: boolean;
  target: InspectorTarget | null;
}): { isOpen: boolean; target: InspectorTarget | null } {
  // Both cleared together, not just `isOpen`: a target left behind while the
  // pref is off is a panel one stray `open` away from showing content the user
  // turned off, and it keeps its subject's queries mounted in the meantime.
  if (!input.enabled) return { isOpen: false, target: null };
  return { isOpen: input.requestedOpen, target: input.target };
}

/**
 * Whether an open panel should adopt the focus store's current selection.
 *
 * "Follows focus" has to mean follows focus CHANGES, not "adopts whatever focus
 * holds whenever the panel opens". An edge is not a focus target, so opening the
 * panel on one leaves the store pointing at the last node clicked — and the
 * looser rule made that stale node replace the edge the user actually clicked.
 *
 * `syncedKey` is the focus the panel has already accounted for, which an
 * explicit `open()` sets too: a caller-supplied target supersedes the focus it
 * was opened over.
 */
export function shouldFollowFocus(input: {
  open: boolean;
  seededKey: string | null;
  syncedKey: string | null;
}): boolean {
  return input.open && input.seededKey !== null && input.syncedKey !== input.seededKey;
}

export interface InspectorController {
  /** What the panel shows; `null` when it has never been opened. */
  target: InspectorTarget | null;
  isOpen: boolean;
  open: (target: InspectorTarget) => void;
  close: () => void;
  /** Mirrors `DrilldownPanel`'s `onOpenChange` (dismiss by overlay / Escape). */
  setOpen: (open: boolean) => void;
  /** False when the surface must not offer the affordance at all. */
  enabled: boolean;
}

export function useInspector(options: { enabled?: boolean } = {}): InspectorController {
  const { enabled = false } = options;
  const focus = useOptionalInvestigationFocus();
  const [target, setTarget] = React.useState<InspectorTarget | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const focused = focus?.focus ?? null;
  const view = resolveInspectorView({ enabled, requestedOpen: isOpen, target });
  const open = view.isOpen;

  // Re-seed an OPEN panel from the focus store. Keyed on the derived target
  // rather than the focus object so an unrelated focus change (a catalog chip,
  // an address with no chain) leaves the current panel alone instead of
  // blanking it.
  const seeded = React.useMemo(() => subjectFromFocus(focused), [focused]);
  const seededKey = seeded ? targetKey(seeded) : null;

  // The focus this panel has already accounted for — see `shouldFollowFocus`.
  const syncedFocus = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!seeded) return;
    if (!shouldFollowFocus({ open, seededKey, syncedKey: syncedFocus.current })) return;
    syncedFocus.current = seededKey;
    setTarget(seeded);
    // `seeded` is derived from `seededKey`; depending on the key keeps this from
    // re-running on every render that rebuilds an equal object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seededKey]);

  const openTarget = React.useCallback(
    (next: InspectorTarget) => {
      if (!enabled) return;
      // An explicit target supersedes the current focus, so record that focus as
      // handled. Otherwise the effect above would treat it as news the next time
      // the panel opens and overwrite what the caller asked for.
      syncedFocus.current = seededKey;
      setTarget(next);
      setIsOpen(true);
    },
    [enabled, seededKey],
  );

  const close = React.useCallback(() => setIsOpen(false), []);

  return React.useMemo<InspectorController>(
    () => ({
      target: view.target,
      isOpen: view.isOpen,
      open: openTarget,
      close,
      setOpen: (next: boolean) => setIsOpen(next && enabled),
      enabled,
    }),
    [close, enabled, openTarget, view.isOpen, view.target],
  );
}
